/**
 * The live dashboard's view: the panels' `CohortView`, built from the
 * Worker's payloads instead of the fixtures (Sep 10 2026, build order item 3).
 *
 * ── ONE SHAPE, TWO SOURCES ───────────────────────────────────────────────
 * Every panel renders a `CohortView`. On /demo it comes from `deriveCohort`
 * over the sample students; on /admin it comes from here, over what the
 * Worker serves for the signed-in institution: the session, the
 * organization row, the billing row, the claim campaign (`/cohort`) and the
 * latest snapshot (`/metrics`). The panels do not know which they got.
 *
 * ── WHAT THE SNAPSHOT DOES NOT CARRY, SAID PLAINLY ───────────────────────
 * The snapshot is built from P3's student records: who named the
 * institution, who holds a mentorship, what they asked for, their industry
 * and education goal and meeting rhythm. Since Sep 11 2026 it also carries
 * the running totals on each record (questions asked, mentor video plays,
 * saves and follows), the milestone records once p3-milestones-reader is
 * bound, app use and applications from the app's analytics, and questions by
 * week and answered from the question records. Pulse AI and pathways are
 * recorded in the app and not yet counted per institution; the snapshot's own
 * `sources` say so, and those figures carry that sentence as a designed
 * state, never a zero, and never the sample's figures. Four states are never
 * conflated here: suppressed
 * (we know it and may not say it), unavailable (we could not ask), a real
 * zero, and not measured.
 *
 * ── NOTHING IS RE-SUPPRESSED ─────────────────────────────────────────────
 * Every figure arrives as a Cell the Worker already passed through the
 * suppression core, and every breakdown arrives as a suppressed result.
 * This file dresses, derives and never widens: a figure derived from two
 * others publishes only when both do, and a difference is withheld when one
 * side is (so a hidden count cannot be worked out by subtraction).
 */

import type { Provenance } from "~/app/components/primitives";
import type {
  BillingPayload,
  CohortPayload,
  MembersPayload,
  MetricsPayload,
  OrganizationPayload,
  SessionPayload,
} from "~/app/lib/api";
import {
  ANSWERS_DEFINITION,
  ANSWERS_LIMITS,
  ANSWERS_SOURCE,
  APP_USE_DEFINITION,
  APP_USE_LIMITS,
  APP_USE_SOURCE,
  APP_USE_SPAN,
  APP_USE_WINDOW,
  DEFAULT_FILTERS,
  NOT_YET_ACTIVE_DEFINITION,
  NOT_YET_ACTIVE_LIMITS,
  RECORD_LIMITS,
  TIMELINE_DEFINITION,
  TIMELINE_KINDS,
  TIMELINE_KIND_LABELS,
  TIMELINE_LIMITS,
  TIMELINE_STAGES,
  TIMELINE_STAGE_LABELS,
  dressDistribution,
  type Activity,
  type ActivityFigure,
  type Answers,
  type AppUse,
  type CareerTimelines,
  type CohortFilters,
  type CohortView,
  type Coverage,
  type Distribution,
  type GrowthPoint,
  type GrowthSeries,
  type Institution,
  type Milestones,
  type SeatMeter,
  type TeamMember,
} from "~/app/lib/demo-data";
import { asOfStamp, formatDate } from "~/app/lib/format";
import { JOIN_ORIGIN } from "~/app/lib/joincode";
import { bandFromWire, type BandId } from "~/app/lib/pricing";
import { roleLabel } from "~/app/lib/session";
import {
  K,
  count,
  rate,
  suppressBuckets,
  suppressed,
  unavailable,
  value,
  type Cell,
  type Rate,
  type SuppressedBuckets,
} from "~/lib/suppression";

export interface LiveInput {
  readonly session: SessionPayload;
  readonly organization: OrganizationPayload;
  /** Null for a viewer: billing is an admin route and a viewer's 403 is the designed answer. */
  readonly billing: BillingPayload | null;
  readonly cohort: CohortPayload;
  /** Null while the first snapshot is pending (the Worker's 503 SNAPSHOT_PENDING). */
  readonly metrics: MetricsPayload | null;
}

/* The unavailable reasons `describeUnavailable` turns into sentences. */
const NO_SNAPSHOT = "no data";
const NOT_MEASURED = "not measured per institution yet";

const DAY_MS = 86_400_000;
const YEAR_MS = 365 * DAY_MS;
const MONTH_MS = 30 * DAY_MS;

const iso = (seconds: number): string => new Date(seconds * 1000).toISOString();

/** The fallback sentence for figures recorded in the app but not yet counted
 *  per institution, when a snapshot carries no reason of its own. */
export const NOT_COUNTED_YET = "Recorded in the app, and not yet counted per institution.";

/* ==========================================================================
   The institution
   ========================================================================== */

export function liveInstitution(input: LiveInput): Institution {
  const { session, organization, billing, cohort } = input;
  // Billing is an admin route, so a viewer arrives with `billing: null`. The
  // session carries the plan's band, interval and renewal for every role, so
  // a viewer's Settings and rail read the real plan and never a made-up date.
  const plan = billing?.plan ?? null;
  const band = bandFromWire(plan?.band ?? session.plan.band ?? organization.capacity_band);
  const capacityBand: BandId = band?.id ?? "quote";
  const interval = plan?.interval ?? session.plan.interval;
  const renewsAtSeconds = plan?.renewsAt ?? session.plan.renewsAt;

  const nowSeconds = Math.floor(Date.now() / 1000);
  // The period start is a billing field. Without it (a viewer), the start is
  // derived from the renewal and the interval: one period back.
  const startMs =
    plan?.periodStart !== null && plan?.periodStart !== undefined
      ? plan.periodStart * 1000
      : renewsAtSeconds !== null
        ? renewsAtSeconds * 1000 - (interval === "month" ? MONTH_MS : YEAR_MS)
        : nowSeconds * 1000;
  const start = new Date(startMs).toISOString();
  const end =
    renewsAtSeconds !== null
      ? iso(renewsAtSeconds)
      : new Date(startMs + (interval === "month" ? MONTH_MS : YEAR_MS)).toISOString();

  const code = cohort.joinCodes.find((c) => c.active) ?? cohort.joinCodes[0] ?? null;
  const location = [organization.address_city, organization.address_state]
    .filter((part): part is string => typeof part === "string" && part.trim() !== "")
    .join(", ");

  return {
    legalName: organization.legal_name,
    displayName: organization.display_name,
    location: location || "Not on file",
    joinSlug: organization.slug,
    joinCode: code?.code ?? "",
    joinUrl: `${new URL(JOIN_ORIGIN).host}/${organization.slug}`,
    // The signed-in admin's own domain. Nothing has verified it; see the
    // field's comment in demo-data.ts.
    teamDomain: session.email.slice(session.email.indexOf("@") + 1),
    capacityBand,
    seatsPurchased: plan?.bandLimit ?? band?.capacity ?? 0,
    planInterval: interval === "month" ? "Monthly" : "Annual",
    subscribedAt: start,
    renewsAt: end,
    // P3 holds no academic calendar for a live tenant. The subscription
    // year is the period P3 can vouch for, and every tenant is in its first.
    term: { name: "Your first year", start, end, isFirst: true },
    enrollmentEstimate: organization.enrollment_estimate,
    viewerRole: roleLabel(session.role),
    contactName: session.memberName ?? session.email,
  };
}

/* ==========================================================================
   Cell arithmetic that cannot widen
   ========================================================================== */

/**
 * A figure derived from two others. Publishes only when both do; withholds
 * as complementary when either is withheld (so a hidden count cannot be
 * recovered from the derived one); stays unavailable when either is.
 */
function derive<A, B>(
  a: Cell<A>,
  b: Cell<B>,
  make: (a: A, b: B) => Cell<number>,
): Cell<number> {
  if (a.kind === "value" && b.kind === "value") return make(a.value, b.value);
  if (a.kind === "unavailable") return a;
  if (b.kind === "unavailable") return b;
  return suppressed("complementary");
}

const rawOf = (cell: Cell<number>): number => (cell.kind === "value" ? cell.value : 0);

/* ==========================================================================
   Breakdowns, dressed
   ========================================================================== */

/**
 * The Worker's claim sources, as the reader's words. `student_claims.source`
 * carries the join code's channel; `app` is the row the Worker adds for the
 * students who chose the institution from the in-app list.
 */
const CHANNEL_LABELS: Readonly<Record<string, string>> = {
  qr: "QR poster",
  link: "Join link",
  advisor: "Advisor email",
  code: "Typed the code",
  "domain-new": "Verified email domain, new account",
  "domain-existing": "Verified email domain, existing account",
  app: "Selected your institution in the app",
};

function relabel(result: SuppressedBuckets, labels: Readonly<Record<string, string>>): SuppressedBuckets {
  return { ...result, shown: result.shown.map((row) => ({ ...row, label: labels[row.label] ?? row.label })) };
}

const EMPTY: SuppressedBuckets = suppressBuckets([]);

/* ==========================================================================
   The view
   ========================================================================== */

export function liveView(input: LiveInput, filters: CohortFilters = DEFAULT_FILTERS): CohortView {
  const institution = liveInstitution(input);
  const { metrics, cohort, session } = input;

  const asOf = metrics ? iso(metrics.capturedAt) : new Date().toISOString();
  // The cadence is a promise the Worker keeps: an hourly cron trigger
  // (wrangler.toml) recomputes every institution, and the header still shows
  // the measured stamp rather than the promise. If the trigger is ever
  // removed, these two lines are what has to change with it.
  const freshness = {
    asOf: asOfStamp(asOf),
    cadence: metrics ? "Recomputed hourly." : "Not computed yet.",
  };
  const freshnessLine = metrics
    ? `${asOfStamp(asOf)} · Recomputed hourly.`
    : "Figures not computed yet.";
  // Under a cohort (Sep 17 2026) the population every figure is over is the
  // cohort's students, and every provenance drawer says so. Under a slice
  // (Sep 18 2026) it is the students the platform says hold a confirmed
  // mentorship, or hold none, and the drawer says that instead: the slice's
  // document replaces the institution's, so every figure on the page is the
  // slice's.
  const cohortLabel = metrics?.cohort?.name;
  const slice = metrics?.slice;
  const sliceLabel = slice?.name;
  const label = cohortLabel
    ? `Every student your team placed in ${cohortLabel}`
    : slice
      ? `Every student who has ever claimed your institution and ${slice.id === "matched" ? "holds a confirmed mentorship" : "holds no confirmed mentorship"}`
      : "Every student who has ever claimed your institution";
  const prov = (p: Omit<Provenance, "freshness" | "window">): Provenance => ({
    ...p,
    window: label,
    freshness,
  });

  /* ---- reach ---- */
  const claimed: Cell<number> = metrics ? metrics.sections.reach.claimed : unavailable(NO_SNAPSHOT);
  const claimedRaw = rawOf(claimed);
  const belowFloor = claimed.kind !== "value" || claimed.value < K;

  const claimedProvenance = prov({
    definition: "A student is counted when they select your institution in the P3 app or use your join code.",
    source: "From P3 student records, not from app activity.",
    windowRule:
      "Weeks end on Sunday, and the current partial week is left out, so a part-week never reads as a collapse.",
    denominator: "Every student record naming your institution. There is no sampling: this is the whole population.",
    limits: [
      "This is a floor, not a register. A student who has not named your institution is invisible here even if they attend.",
      "A student can change the institution on their profile at any time, and their record moves with them.",
    ],
  });

  /* ---- mentorship ---- */
  const matchedCell: Cell<Rate> = metrics ? metrics.sections.engagement.matched.cell : unavailable(NO_SNAPSHOT);
  const matchedCount: Cell<number> =
    matchedCell.kind === "value" ? count(matchedCell.value.numerator) : matchedCell;
  const mentorshipsHeld: Cell<number> = metrics
    ? metrics.sections.engagement.mentorshipsHeld
    : unavailable(NO_SNAPSHOT);
  const perMatched = derive(matchedCount, mentorshipsHeld, (matched, held) =>
    matched > 0 ? value(Math.round((held / matched) * 10) / 10) : unavailable("no denominator"),
  );
  const waitingWithRequest: Cell<number> = metrics
    ? metrics.sections.engagement.waitingWithRequest
    : unavailable(NO_SNAPSHOT);
  const waitingTotal = derive(claimed, matchedCount, (c, m) => count(Math.max(0, c - m)));
  const waitingWithoutRequest = derive(waitingTotal, waitingWithRequest, (t, w) => count(Math.max(0, t - w)));

  /* ---- growth ---- */
  const growth = toGrowth(
    metrics?.sections.reach.weekly.points ?? [],
    prov({
      definition: "The running total of students claimed to your institution, by week.",
      source: "From P3 student records, by the date each student's account was created.",
      windowRule: "Weeks end on Sunday. The current partial week is not drawn, because a part-week reads as a collapse.",
      denominator: "Every student claimed to your institution.",
      limits: [
        "A week whose running total is under 10 students is not plotted. The curve starts at the first week that publishes.",
        "P3 began keeping dated snapshots when your subscription started, so the curve reaches back only that far.",
      ],
    }),
  );

  /* ---- seat and coverage ---- */
  const seat: SeatMeter = {
    purchased: institution.seatsPurchased,
    claimed,
    claimedRaw,
    available: derive(value(institution.seatsPurchased), claimed, (p, c) => count(Math.max(0, p - c))),
    fraction:
      institution.seatsPurchased > 0 ? Math.min(claimedRaw / institution.seatsPurchased, 1) : 0,
    overCapacity: claimedRaw > institution.seatsPurchased && institution.seatsPurchased > 0,
    renewsAt: institution.renewsAt,
    visibleToViewer: session.role !== "viewer",
  };

  const estimate = institution.enrollmentEstimate;
  const coverage: Coverage = {
    available: estimate !== null,
    estimate,
    claimed,
    rate:
      estimate === null
        ? unavailable("no denominator")
        : claimed.kind === "value"
          ? rate(claimed.value, estimate)
          : claimed,
    provenance: prov({
      definition: "The share of the students your team estimated who have connected a P3 account to your institution.",
      source: "From P3 student records over a figure your team gave P3.",
      denominator: "Over the enrollment estimate your team gave P3, shown in Settings.",
      limits: [
        "The numerator is exact. The denominator is yours, and P3 cannot check it.",
        "Students who have not named your institution are still using P3. Their activity is simply not attributed to you.",
      ],
    }),
  };

  /* ---- breakdowns ---- */
  const channels = dressDistribution(relabel(cohort.sources, CHANNEL_LABELS), {
    key: "channels",
    unit: "students",
    cap: 6,
    footnote: "Students who chose you in the app left no join event, so they have their own row.",
    provenance: prov({
      definition: "How each student reached your roster.",
      source: "From P3 join events and student records.",
      denominator:
        "Every student claimed to your institution. A student who chose you from the in-app list left no join event, so they have their own row.",
      limits: ["P3 cannot attribute a student who selected your institution from the in-app list to a campaign."],
    }),
  });

  const mentorshipCounts = dressDistribution(metrics ? metrics.sections.engagement.mentorshipCounts : EMPTY, {
    key: "mentorship-counts",
    unit: "students",
    keepOrder: true,
    footnote: "Mentorships exceed matched students because some students have more than one mentor.",
    provenance: prov({
      definition: "How many confirmed mentorships each student holds.",
      source: "From P3 student records.",
      denominator: "Every student claimed to your institution.",
      limits: [
        "In a small cohort the two-or-more group can fall under the reporting threshold and roll up. That is the suppression rule working, not a gap in the data.",
      ],
    }),
  });

  const supportAreas = dressDistribution(metrics ? metrics.sections.engagement.wants : EMPTY, {
    key: "support-areas",
    unit: "students",
    cap: 6,
    footnote: "Students pick one or more areas, so the rows sum to more than students.",
    provenance: prov({
      definition: "The support areas your students selected on their P3 profile.",
      source: "From P3 student records.",
      denominator: "Every student claimed to your institution, counted once per area they selected.",
      limits: ["This is what students say they want, not what they have received."],
    }),
  });

  const industries = dressDistribution(metrics ? metrics.sections.composition.industry : EMPTY, {
    key: "industries",
    unit: "students",
    cap: 6,
    footnote: "One industry per student.",
    provenance: prov({
      definition: "The industry each student named an interest in.",
      source: "From P3 student records.",
      denominator: "Every student claimed to your institution who has set an industry.",
    }),
  });

  const educationGoals = dressDistribution(metrics ? metrics.sections.composition.education : EMPTY, {
    key: "education-goals",
    unit: "students",
    cap: 6,
    footnote: "Each student selects one education goal.",
    provenance: prov({
      definition: "The education goal each student selected.",
      source: "From P3 student records.",
      denominator: "Every student claimed to your institution who has set a goal.",
      limits: [
        "A goal is what a student intends. P3 holds no enrollment, credit or degree record and cannot tell you whether they got there.",
      ],
    }),
  });

  const meetingRhythms = dressDistribution(metrics ? metrics.sections.composition.meetFrequency : EMPTY, {
    key: "meeting-rhythms",
    unit: "students",
    keepOrder: true,
    footnote: "One rhythm per student: the mentor time your cohort expects.",
    provenance: prov({
      definition: "The meeting rhythm each student asked for.",
      source: "From P3 student records.",
      denominator: "Every student claimed to your institution.",
      limits: ["A stated preference, not an observed cadence. P3 does not record whether a meeting happened."],
    }),
  });

  /* ---- activity: the record totals, app use and applications from the
     app's analytics, and questions from the question records are measured;
     Pulse AI and pathways are recorded in the app and not yet counted per
     institution ---- */
  const app = liveApp(metrics);
  const appActivity = metrics?.sources["appActivity"];
  const notMeasuredReason =
    appActivity?.state === "notMeasured" && appActivity.reason
      ? appActivity.reason
      : metrics
        ? NOT_COUNTED_YET
        : "P3 has not computed your figures yet.";
  // Pulse AI and pathways carry their own sentence from Sep 11 2026; a
  // snapshot written before then has only the app-activity one.
  const pulseSource = metrics?.sources["pulseAndPathways"];
  const pulseReason =
    pulseSource?.state === "notMeasured" && pulseSource.reason ? pulseSource.reason : notMeasuredReason;
  // Milestones carry their own sentence from Sep 11 2026; a snapshot written
  // before then has only the app-activity one.
  const milestonesSource = metrics?.sources["milestones"];
  const milestonesReason =
    milestonesSource?.state === "notMeasured" && milestonesSource.reason
      ? milestonesSource.reason
      : notMeasuredReason;

  const activityProvenance = prov({
    definition: "What your students do on P3 without waiting for a match.",
    source: "Recorded in the app, and not yet counted per institution.",
    denominator: "Every student claimed to your institution.",
    limits: [app.state === "notCounted" ? notMeasuredReason : pulseReason],
  });
  /* Pulse AI on a live tenant (Pipe D, Sep 12 2026). Every limit here is a
     fact about how the figure is made, and each one is checked by a test in
     p3-web-store's aggregate suite: a live session holds something the
     student wrote, a deleted one does not count, the content never leaves the
     store, and Interview Prep is a separate mode. */
  const pulseProvenance = prov({
    definition:
      "Conversations your students have had with Pulse AI, the career coach in the app: the sessions, and the students who had them.",
    source: "Counted each hour from P3's web store, by student. The conversations themselves are never read into this dashboard.",
    denominator: "Every student claimed to your institution.",
    limits: [
      "A session counts once your student has written in it. One Pulse opened and the student never answered is not counted, and neither is one the student deleted.",
      "Interview Prep is a separate mode and is not included.",
      "Pulse AI is offered to students 18 and over.",
    ],
  });
  // App use, applications and questions by week cover the last 90 days, not
  // every student's whole record, so their window says so.
  const opportunitiesProvenance: Provenance = {
    ...prov({
      definition:
        "Opportunities your students applied to through the app in the last 90 days: scholarships, internships and jobs. In the app, opening the provider's application is the application.",
      source: APP_USE_SOURCE,
      denominator: "Every student claimed to your institution.",
      limits: [
        "P3 knows a student applied through the app. It does not know the result, and it holds no record from the scholarship provider or the employer.",
        "Opening an opportunity without applying is not counted per institution yet.",
        ...APP_USE_LIMITS,
      ],
    }),
    window: APP_USE_WINDOW,
  };
  const appUseProvenance: Provenance = {
    ...prov({
      definition: APP_USE_DEFINITION,
      source: APP_USE_SOURCE,
      denominator: "Every student claimed to your institution.",
      limits: [...APP_USE_LIMITS],
    }),
    window: APP_USE_WINDOW,
  };
  const answersProvenance: Provenance = {
    ...prov({
      definition: ANSWERS_DEFINITION,
      source: ANSWERS_SOURCE,
      denominator: "Every question asked by a student claimed to your institution.",
      limits: [...ANSWERS_LIMITS],
    }),
    window: APP_USE_WINDOW,
  };
  const milestonesProvenance = prov({
    definition: "The milestones your students mark complete and the outcomes they report.",
    source: "Recorded in the app as students mark milestones complete, and not yet counted per institution.",
    denominator: "Every student claimed to your institution.",
    limits: [milestonesReason, "Reported by the student, not verified by P3."],
  });
  const questionsProvenance = prov({
    definition: "Questions your students have asked P3 mentors, directly or in the open question pool.",
    source: "From P3 student records: a running total on each student's record.",
    denominator: "Every student claimed to your institution.",
    limits: [
      "Questions are asked in the student's own words. P3 shows you the count, never the text.",
      ...RECORD_LIMITS,
    ],
  });
  const videosProvenance = prov({
    definition: "Mentor videos your students played and saved, and the mentors they follow.",
    source: "From P3 student records: running totals on each student's record.",
    denominator: "Every student claimed to your institution.",
    limits: [...RECORD_LIMITS],
  });
  const notYetActiveProvenance = prov({
    definition: NOT_YET_ACTIVE_DEFINITION,
    source: "From P3 student records.",
    denominator: "Every student claimed to your institution.",
    limits: [...NOT_YET_ACTIVE_LIMITS],
  });

  /* ---- comparison ---- */
  const platformMatched: Cell<Rate> = metrics ? metrics.platform.matched.cell : unavailable(NO_SNAPSHOT);

  return {
    scenario: "established",
    institution,
    filters,
    filtersActive: false,
    windowLabel: label,
    cohortSize: claimedRaw,
    belowFloor,
    claimed,
    claimedInView: claimedRaw,
    claimedProvenance,
    growth,
    seat,
    coverage,
    channels,
    matchedCount,
    matchedRate: matchedCell,
    mentorshipsHeld,
    perMatched,
    matchedProvenance: prov({
      definition: "A student counts here when they hold at least one confirmed mentorship.",
      source: "From P3 student records, measured on each student's own record.",
      denominator: "Every student claimed to your institution.",
      limits: [
        "Never derived by subtracting mentor-side counts: students hold more than one mentorship.",
        "Holding a mentorship is not the same as meeting regularly. P3 does not observe what happens in a mentorship.",
      ],
    }),
    waitingTotal,
    waitingWithRequest,
    waitingWithoutRequest,
    waitingRaw: rawOf(waitingTotal),
    waitingProvenance: prov({
      definition: "Students with no confirmed mentorship, split by whether they have asked for one.",
      source: "From P3 student records.",
      denominator: "Every student claimed to your institution with no confirmed mentorship.",
      limits: ["P3 does not tell you which students these are, and no export contains a student row."],
    }),
    mentorshipCounts,
    supportAreas,
    industries,
    educationGoals,
    meetingRhythms,
    activity: liveActivity(
      metrics,
      app,
      { notCounted: notMeasuredReason, pulse: pulseReason },
      {
        activity: activityProvenance,
        opportunities: opportunitiesProvenance,
        appUse: appUseProvenance,
        answers: answersProvenance,
        questions: questionsProvenance,
        videos: videosProvenance,
        notYetActive: notYetActiveProvenance,
        pulse: pulseProvenance,
      },
    ),
    milestones: milestonesView(
      metrics,
      { reason: milestonesReason, provenance: milestonesProvenance },
      prov,
      growth.provenance,
    ),
    timelines: timelinesView(metrics, prov),
    comparisonAvailable: {
      none: { ok: true },
      prev: {
        ok: false,
        reason: "This is your first year on P3, so there is no earlier period to compare with. It turns on after your first renewal.",
      },
      platform:
        platformMatched.kind === "value"
          ? { ok: true }
          : { ok: false, reason: "The comparison arrives with your first figures." },
    },
    platform: {
      matched: platformMatched,
      label: "All students on P3",
      group: "all students on P3",
      note: metrics ? `Across P3, ${asOfStamp(asOf, { prefix: "as of" })}` : "Across P3",
      // Every student on P3, the institution's own included, from the same
      // read as the institution's figures. Said on the card so the reader
      // knows the comparison group contains the group being compared.
      footnote:
        "The comparison group is every student on P3, your own students included, from the same read as your figures.",
    },
    asOf,
    freshnessLine,
    stale: metrics?.stale ?? false,
    held: metrics?.held ?? false,
    ...(cohortLabel ? { cohortLabel } : {}),
    ...(sliceLabel ? { sliceLabel } : {}),
  };
}

/* ==========================================================================
   Activity: the record totals, and what P3 does not count per institution yet
   ========================================================================== */

function emptyDistribution(key: string, provenance: Provenance): Distribution {
  return dressDistribution(EMPTY, { key, unit: "students", footnote: "", provenance });
}

function unmeasuredFigure(): ActivityFigure {
  return {
    total: unavailable(NOT_MEASURED),
    students: unavailable(NOT_MEASURED),
    studentsRaw: 0,
    share: unavailable(NOT_MEASURED),
  };
}

type WireActivity = NonNullable<MetricsPayload["sections"]["activity"]>;

/**
 * A record total as the Worker published it. The Worker sends no count
 * under the floor, so the raw behind a threshold meter is the published
 * value or zero, and a withheld figure draws no meter (ActivityPanel).
 */
function recordFigure(figure: WireActivity["questions"] | undefined, missing: string): ActivityFigure {
  if (!figure) {
    const cell = unavailable(missing);
    return { total: cell, students: cell, studentsRaw: 0, share: cell };
  }
  return {
    total: figure.total,
    students: figure.students,
    studentsRaw: rawOf(figure.students),
    share: figure.share,
  };
}

type WireApp = NonNullable<MetricsPayload["sections"]["appActivity"]>;
type WireQuestions = NonNullable<MetricsPayload["sections"]["questionsOverTime"]>;

/** App activity on the last refresh: read, not read, or not counted per institution. */
type LiveApp =
  | { readonly state: "read"; readonly section: WireApp }
  | { readonly state: "unread"; readonly reason: string }
  | { readonly state: "notCounted" };

/** The Worker sends its own sentences; these stand in only if one is missing. */
const APP_UNREAD_FALLBACK = "P3 could not read app activity on the last refresh.";
const QUESTIONS_UNREAD_FALLBACK = "P3 could not read the question records on the last refresh.";

function liveApp(metrics: MetricsPayload | null): LiveApp {
  const source = metrics?.sources["appActivity"];
  const section = metrics?.sections.appActivity;
  if (source?.state === "ok") return section ? { state: "read", section } : { state: "unread", reason: APP_UNREAD_FALLBACK };
  if (source?.state === "unavailable") return { state: "unread", reason: source.reason ?? APP_UNREAD_FALLBACK };
  return { state: "notCounted" };
}

/** App use as the Worker published it, or the state that says why not. */
function liveAppUse(app: LiveApp, provenance: Provenance): AppUse {
  if (app.state === "read") {
    const section = app.section;
    return {
      active: section.activeStudents,
      activeRaw: rawOf(section.activeStudents),
      activeShare: section.activeShare,
      sessionsPerStudent: section.sessionsPerStudent,
      averageSessionSeconds: section.averageSessionSeconds,
      span: APP_USE_SPAN,
      provenance,
    };
  }
  const missing = unavailable(app.state === "unread" ? NOT_READ : NOT_MEASURED);
  return {
    active: missing,
    activeRaw: 0,
    activeShare: missing,
    sessionsPerStudent: missing,
    averageSessionSeconds: missing,
    span: APP_USE_SPAN,
    provenance,
    ...(app.state === "unread" ? { unread: app.reason } : {}),
  };
}

/** Questions by week and answered, as the Worker read them, or why not. */
function liveAnswers(metrics: MetricsPayload | null, provenance: Provenance): Answers {
  const source = metrics?.sources["questions"];
  const section: WireQuestions | undefined = metrics?.sections.questionsOverTime;
  if (source?.state === "ok" && section) {
    return {
      asked: recordFigure(section.asked, NO_SNAPSHOT),
      answered: section.answered,
      weekly: section.weekly.points,
      span: APP_USE_SPAN,
      provenance,
    };
  }
  if (source?.state === "ok" || source?.state === "unavailable") {
    return {
      asked: recordFigure(undefined, NOT_READ),
      answered: unavailable(NOT_READ),
      weekly: [],
      span: APP_USE_SPAN,
      provenance,
      unread: source.reason ?? QUESTIONS_UNREAD_FALLBACK,
    };
  }
  const missing = metrics ? NOT_MEASURED : NO_SNAPSHOT;
  return {
    asked: recordFigure(undefined, missing),
    answered: unavailable(missing),
    weekly: [],
    span: APP_USE_SPAN,
    provenance,
    notMeasured: NOT_COUNTED_YET,
  };
}

function liveActivity(
  metrics: MetricsPayload | null,
  app: LiveApp,
  reasons: { readonly notCounted: string; readonly pulse: string },
  provenance: {
    readonly activity: Provenance;
    readonly opportunities: Provenance;
    readonly appUse: Provenance;
    readonly answers: Provenance;
    readonly questions: Provenance;
    readonly videos: Provenance;
    readonly notYetActive: Provenance;
    readonly pulse: Provenance;
  },
): Activity {
  // Absent on a snapshot written before Sep 11 2026, served until the next
  // hourly tick replaces it: the section then keeps its one sentence.
  const records = metrics?.sections.activity;
  const idleShare: Cell<Rate> = records ? records.notYetActive : unavailable(NO_SNAPSHOT);
  const idle: Cell<number> =
    idleShare.kind === "value" ? count(idleShare.value.numerator) : idleShare;
  // Applications and opens both come from the app's analytics (opens since
  // Sep 19 2026). The RATE between the two is still not published: it needs
  // its own floor argument over two event counts, not one.
  const applied: ActivityFigure =
    app.state === "read"
      ? recordFigure(app.section.applications, NO_SNAPSHOT)
      : app.state === "unread"
        ? recordFigure(undefined, NOT_READ)
        : unmeasuredFigure();
  /* PULSE, FROM THE WEB STORE (Pipe D, Sep 12 2026). Counted only when the
     snapshot carries the section AND its source read ok: a section left over
     from an earlier tick must not stand in for a read that failed on this one.
     The pair is the one the Pulse card already draws, sessions and the
     students who had them. Rendered only while app activity is counted, which
     is the state the Pulse card lives in; a tenant whose analytics are not
     configured keeps the single sentence for every figure. */
  const careerTimeline = metrics?.sections.careerTimeline;
  const pulseCounted =
    app.state !== "notCounted" &&
    careerTimeline !== undefined &&
    metrics?.sources["careerTimeline"]?.state === "ok";
  const opened: ActivityFigure =
    app.state === "read"
      ? recordFigure(app.section.opportunitiesOpened, NO_SNAPSHOT)
      : app.state === "unread"
        ? recordFigure(undefined, NOT_READ)
        : unmeasuredFigure();
  return {
    opportunitiesOpened: opened,
    opportunitiesApplied: applied,
    applyRate: unavailable(NOT_MEASURED),
    questions: recordFigure(records?.questions, NO_SNAPSHOT),
    pulse: pulseCounted && careerTimeline ? recordFigure(careerTimeline.pulse, NO_SNAPSHOT) : unmeasuredFigure(),
    adults: unavailable(NOT_MEASURED),
    adultsRaw: 0,
    pathways: unmeasuredFigure(),
    pathwaysExplored: emptyDistribution("pathways", provenance.activity),
    videoPlays: recordFigure(records?.videoPlays, NO_SNAPSHOT),
    videosSaved: recordFigure(records?.videosSaved, NO_SNAPSHOT),
    mentorsFollowed: recordFigure(records?.mentorsFollowed, NO_SNAPSHOT),
    notYetActive: idle,
    notYetActiveShare: idleShare,
    notYetActiveRaw: rawOf(idle),
    recordsMeasured: records !== undefined,
    provenance: app.state === "notCounted" ? provenance.activity : provenance.opportunities,
    questionsProvenance: provenance.questions,
    pulseProvenance: pulseCounted ? provenance.pulse : provenance.activity,
    pathwaysProvenance: provenance.activity,
    videosProvenance: provenance.videos,
    notYetActiveProvenance: provenance.notYetActive,
    appUse: liveAppUse(app, provenance.appUse),
    answers: liveAnswers(metrics, provenance.answers),
    // While app activity is not counted, one card carries every figure's
    // sentence. Once it is, the tenant is live, pathways keep a sentence of
    // their own, and so does Pulse until the web store has been read.
    ...(app.state === "notCounted"
      ? { notMeasured: reasons.notCounted }
      : {
          liveTenant: true as const,
          pathwaysNotMeasured: reasons.pulse,
          ...(pulseCounted ? {} : { pulseNotMeasured: reasons.pulse }),
        }),
  };
}

/* ==========================================================================
   Milestones: read from the milestone records each hour (Sep 11 2026), or
   the sentence that says why not
   ========================================================================== */

type WireMilestones = NonNullable<MetricsPayload["sections"]["milestones"]>;
type WirePoint = MetricsPayload["sections"]["reach"]["weekly"]["points"][number];
type Prov = (p: Omit<Provenance, "freshness" | "window">) => Provenance;

/** The unavailable reason on a figure the last refresh could not read. */
const NOT_READ = "not read on the last refresh";

/** A live tenant's milestones span each student's record, not a window. */
const LIVE_SPAN = "since each student joined P3";

/** The Worker sends its own sentence; this stands in only if one is missing. */
const UNREAD_FALLBACK = "P3 could not read the milestone records on the last refresh.";

/**
 * A weekly running total as the Worker published it, as the panels' series.
 * The label is formatted from the calendar date at midday Central, so it
 * cannot slip a day in another zone; the weekly addition is derived and
 * never widens; only the weeks that publish are drawn.
 */
function toGrowth(weekly: readonly WirePoint[], provenance: Provenance): GrowthSeries {
  const points: GrowthPoint[] = weekly.map((p, i, all) => {
    const previous: Cell<number> = i > 0 ? all[i - 1]!.cumulative : value(0);
    return {
      weekEnding: p.weekEnding,
      label: formatDate(`${p.weekEnding}T12:00:00-05:00`, { style: "short" }),
      cumulative: p.cumulative,
      added: derive(p.cumulative, previous, (now, before) => count(Math.max(0, now - before))),
    };
  });
  const drawable = points.flatMap((p) =>
    p.cumulative.kind === "value" ? [{ label: p.label, value: p.cumulative.value }] : [],
  );
  return { points, drawable, suppressedWeeks: points.length - drawable.length, annotations: [], provenance };
}

/**
 * The Milestones section, in one of three states that are never conflated:
 * read (the snapshot carries the section and its source is ok); not read on
 * the last refresh (the source is unavailable: the reader failed, and the
 * next hourly refresh tries again); and not counted per institution yet (the
 * reader is not bound, or the snapshot predates it).
 */
function milestonesView(
  metrics: MetricsPayload | null,
  notCounted: { readonly reason: string; readonly provenance: Provenance },
  prov: Prov,
  weekly: Provenance,
): Milestones {
  const source = metrics?.sources["milestones"];
  const section = metrics?.sections.milestones;
  if (source?.state === "ok" && section) return readMilestones(section, prov);
  if (source?.state === "unavailable") {
    const reason = source.reason ?? UNREAD_FALLBACK;
    const provenance = prov({
      definition: "The milestones your students mark complete and the outcomes they report.",
      source: "From P3 milestone records, read each hour.",
      denominator: "Every student claimed to your institution.",
      limits: [reason, "Reported by the student, not verified by P3."],
    });
    return absentMilestones({ unread: reason }, NOT_READ, provenance, weekly);
  }
  return absentMilestones({ notMeasured: notCounted.reason }, NOT_MEASURED, notCounted.provenance, weekly);
}

/**
 * The section as the Worker published it. Every figure arrives suppressed,
 * so the raw behind a threshold meter is the published value or zero, and a
 * withheld figure draws no meter (MilestonesPanel).
 */
function readMilestones(section: WireMilestones, prov: Prov): Milestones {
  const follows =
    "A student's milestones follow the student: one marked complete before they named your institution is counted here, and moves with them if they change it.";
  const provenance = prov({
    definition: "The milestones your students have marked complete in the app, each student counted once per milestone.",
    source: "From P3 milestone records, read each hour.",
    denominator: "Every student claimed to your institution.",
    limits: [follows, "Reported by the student, not verified by P3."],
  });
  const outcomesProvenance = prov({
    definition:
      "Outcome milestones your students reported: starting a program, declaring a major, completing an internship, landing a first full-time role.",
    source: "From P3 milestone records, read each hour. Each one asks the student to say where.",
    denominator: "Every student claimed to your institution who reported at least one outcome milestone.",
    limits: [
      "The note a student writes stays private to the student and their mentor. Only the count reaches this page.",
      "Student reported and unverified. P3 holds no registrar, enrollment or employer record.",
      follows,
    ],
  });
  const weekly = prov({
    definition: "The running total of milestones your students have marked complete, by week.",
    source: "From P3 milestone records, by the date each was marked complete.",
    windowRule: "Weeks end on Sunday. The current partial week is not drawn, because a part-week reads as a collapse.",
    denominator: "Every milestone marked complete by a student claimed to your institution.",
    limits: [
      "A week is plotted once ten students stand behind its running total, because a count of completions is a count of the people behind it.",
      follows,
    ],
  });

  const completed = section.completed.total;
  const students = section.completed.students;
  return {
    completed,
    completedRaw: rawOf(completed),
    students,
    studentsRaw: rawOf(students),
    rate: section.completed.share,
    // Derived here and never sent: a ratio is not a count of people, and the
    // Worker's guard cannot tell one from a count. It publishes only when the
    // total and the students both do.
    perStudent: derive(completed, students, (total, n) =>
      n > 0 ? value(Math.round((total / n) * 10) / 10) : unavailable("no denominator"),
    ),
    top: dressDistribution(section.top, {
      key: "milestones",
      unit: "students",
      cap: 6,
      footnote: "Each row counts students, once per milestone, so the rows sum to more than students.",
      provenance,
    }),
    byWeek: toGrowth(section.weekly.points, weekly),
    outcomes: dressDistribution(section.outcomes, {
      key: "outcomes",
      unit: "students",
      keepOrder: true,
      footnote: "In the order a student reaches them. Student reported when marked complete.",
      provenance: outcomesProvenance,
    }),
    progress: dressDistribution(section.progress, {
      key: "progress",
      unit: "students",
      keepOrder: true,
      footnote: "Every claimed student, by milestones marked complete since joining P3.",
      provenance,
    }),
    outcomeStudents: section.outcomeStudents,
    outcomeStudentsRaw: rawOf(section.outcomeStudents),
    fieldMatch: section.fieldMatch,
    provenance,
    outcomesProvenance,
    span: LIVE_SPAN,
  };
}

function absentMilestones(
  why: { readonly notMeasured: string } | { readonly unread: string },
  reason: string,
  provenance: Provenance,
  weekly: Provenance,
): Milestones {
  const missing = unavailable(reason);
  return {
    completed: missing,
    completedRaw: 0,
    students: missing,
    studentsRaw: 0,
    rate: missing,
    perStudent: missing,
    top: emptyDistribution("milestones", provenance),
    byWeek: { points: [], drawable: [], suppressedWeeks: 0, annotations: [], provenance: weekly },
    outcomes: emptyDistribution("outcomes", provenance),
    progress: emptyDistribution("progress", provenance),
    outcomeStudents: missing,
    outcomeStudentsRaw: 0,
    fieldMatch: missing,
    provenance,
    outcomesProvenance: provenance,
    span: LIVE_SPAN,
    ...why,
  };
}

/* ==========================================================================
   Career Timelines: read from the web store each hour (Pipe D, Sep 12 2026),
   or the sentence that says why not
   ========================================================================== */

/** The Worker sends its own sentence; these stand in only if one is missing. */
const TIMELINES_NOT_COUNTED =
  "Career timelines are recorded in P3, and are not yet counted per institution.";
const TIMELINES_UNREAD = "P3 could not read the career timelines on the last refresh.";

/** A group's rows in the reading order (past, current, planned), whatever
 *  order the Worker sorted them in. Order moves no figure. */
function inOrder(result: SuppressedBuckets, order: readonly string[]): SuppressedBuckets {
  const rank = (label: string): number => {
    const i = order.indexOf(label);
    return i === -1 ? order.length : i;
  };
  return { ...result, shown: [...result.shown].sort((a, b) => rank(a.label) - rank(b.label)) };
}

/**
 * The Career Timeline card, in one of three states that are never conflated,
 * as with milestones: read (the section is on the snapshot and its source is
 * ok), not read on the last refresh (the source is unavailable), and not
 * counted per institution yet (the web store is not bound, or the snapshot
 * predates it). A null the store withheld arrives suppressed and stays so.
 */
function timelinesView(metrics: MetricsPayload | null, prov: Prov): CareerTimelines {
  const source = metrics?.sources["careerTimeline"];
  const section = metrics?.sections.careerTimeline;
  const provenance = prov({
    definition: TIMELINE_DEFINITION,
    source: "From the Career Timeline each student keeps in P3, read each hour.",
    denominator: "Every student claimed to your institution.",
    limits: TIMELINE_LIMITS,
  });
  const group = (
    result: SuppressedBuckets,
    labels: Readonly<Record<string, string>>,
    keys: readonly string[],
    key: string,
    footnote: string,
  ): Distribution =>
    dressDistribution(inOrder(relabel(result, labels), keys.map((k) => labels[k] ?? k)), {
      key,
      unit: "entries",
      keepOrder: true,
      footnote,
      provenance,
    });

  if (source?.state === "ok" && section) {
    const students = section.withTimeline;
    return {
      withTimeline: students,
      studentsRaw: students.kind === "value" ? students.value.numerator : 0,
      entries: recordFigure(section.entries, NO_SNAPSHOT),
      byKind: group(
        section.byKind,
        TIMELINE_KIND_LABELS,
        TIMELINE_KINDS,
        "timeline-kind",
        "Entries, not students: a student with a school and a role is in both rows.",
      ),
      byStage: group(
        section.byStage,
        TIMELINE_STAGE_LABELS,
        TIMELINE_STAGES,
        "timeline-stage",
        "Planned is a school or role the student intends, in their own words.",
      ),
      provenance,
    };
  }

  const unread = source?.state === "unavailable" ? (source.reason ?? TIMELINES_UNREAD) : null;
  const missing = unavailable(unread ? NOT_READ : NOT_MEASURED);
  return {
    withTimeline: missing,
    studentsRaw: 0,
    entries: unread ? recordFigure(undefined, NOT_READ) : unmeasuredFigure(),
    byKind: emptyDistribution("timeline-kind", provenance),
    byStage: emptyDistribution("timeline-stage", provenance),
    provenance,
    ...(unread
      ? { unread }
      : {
          notMeasured:
            source?.state === "notMeasured" && source.reason ? source.reason : TIMELINES_NOT_COUNTED,
        }),
  };
}

/* ==========================================================================
   The team
   ========================================================================== */

export function liveTeam(members: MembersPayload, session: SessionPayload): readonly TeamMember[] {
  return members.members.map((m) => ({
    id: m.id,
    name: m.name,
    email: m.email,
    role: roleLabel(m.role),
    status: m.status === "active" ? "Active" : "Invited",
    lastSeen: m.last_seen_at ? iso(m.last_seen_at) : null,
    you: m.id === session.memberId,
  }));
}
